import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Users, TrendingUp, DollarSign, ChevronDown, ChevronRight, Search, CheckCircle2, Clock, Filter, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DateRangePicker from './DateRangePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MySplits({ userInfo }) {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [expandedSplits, setExpandedSplits] = useState({});
  
  // Agents table state
  const [agentSearch, setAgentSearch] = useState('');
  const [agentDateRange, setAgentDateRange] = useState(null);
  const [agentPage, setAgentPage] = useState(1);
  const [agentsPerPage, setAgentsPerPage] = useState(20);
  
  // Splits table state
  const [splitSearch, setSplitSearch] = useState('');
  const [splitStatus, setSplitStatus] = useState([]);
  const [splitPaidStatus, setSplitPaidStatus] = useState([]);
  const [splitDateRange, setSplitDateRange] = useState(null);
  const [splitPage, setSplitPage] = useState(1);
  const [splitsPerPage, setSplitsPerPage] = useState(20);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalSplits: 0,
    totalAnnualPremium: 0,
    approved: { count: 0, premium: 0 },
    underwriting: { count: 0, premium: 0 },
    followUp: { count: 0, premium: 0 },
    cancelledBeforeDraft: { count: 0, premium: 0 },
    declined: { count: 0, premium: 0 },
    lapsedPending: { count: 0, premium: 0 },
    lapsed: { count: 0, premium: 0 },
    notTaken: { count: 0, premium: 0 }
  });

  const calculateDashboardStats = (splitsData) => {
    let totalSplits = splitsData.length;
    let totalAnnualPremium = 0;
    let approved = { count: 0, premium: 0 };
    let underwriting = { count: 0, premium: 0 };
    let followUp = { count: 0, premium: 0 };
    let cancelledBeforeDraft = { count: 0, premium: 0 };
    let declined = { count: 0, premium: 0 };
    let lapsedPending = { count: 0, premium: 0 };
    let lapsed = { count: 0, premium: 0 };
    let notTaken = { count: 0, premium: 0 };

    splitsData.forEach(split => {
      const policy = split._policydata_of_split?.[0] || {};
      const premium = parseFloat(policy.annual_premium) || 0;
      totalAnnualPremium += premium;
      
      const status = (policy.policyStatus || '').toLowerCase();
      
      if (status.includes('approved') || status.includes('active')) {
        approved.count++;
        approved.premium += premium;
      } else if (status.includes('underwriting')) {
        underwriting.count++;
        underwriting.premium += premium;
      } else if (status.includes('follow')) {
        followUp.count++;
        followUp.premium += premium;
      } else if (status.includes('cancelled before draft')) {
        cancelledBeforeDraft.count++;
        cancelledBeforeDraft.premium += premium;
      } else if (status.includes('declined')) {
        declined.count++;
        declined.premium += premium;
      } else if (status.includes('lapsed pending')) {
        lapsedPending.count++;
        lapsedPending.premium += premium;
      } else if (status === 'lapsed') {
        lapsed.count++;
        lapsed.premium += premium;
      } else if (status.includes('not taken')) {
        notTaken.count++;
        notTaken.premium += premium;
      }
    });

    return { 
      totalSplits, 
      totalAnnualPremium, 
      approved, 
      underwriting, 
      followUp, 
      cancelledBeforeDraft,
      declined,
      lapsedPending,
      lapsed,
      notTaken
    };
  };

  const fetchSplits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/portal/my-splits`, {
        params: { agent_id: userInfo?.agent_id }
      });
      
      const splitsData = Array.isArray(response.data) ? response.data : [];
      setSplits(splitsData);
      
      // Calculate dashboard stats
      const stats = calculateDashboardStats(splitsData);
      setDashboardStats(stats);
      
    } catch (err) {
      console.error('Error fetching splits:', err);
      toast.error('Failed to load splits');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.agent_id]);

  useEffect(() => {
    if (userInfo?.agent_id) {
      fetchSplits();
    }
  }, [userInfo?.agent_id, fetchSplits]);

  // Group by policy agent owner (from _policydata_of_split)
  const agentsData = useMemo(() => {
    const grouped = {};
    
    splits.forEach(split => {
      const policy = split._policydata_of_split?.[0] || {};
      // Group by policy agent owner
      const agentName = policy.policyAgentowner_name || 'Unknown Agent';
      const agentId = policy.ref_agent_owner || 'unknown';
      
      if (!grouped[agentId]) {
        grouped[agentId] = {
          id: agentId,
          name: agentName,
          totalSplits: 0,
          totalPremium: 0,
          statusCount: {}
        };
      }
      
      grouped[agentId].totalSplits++;
      grouped[agentId].totalPremium += parseFloat(policy.annual_premium) || 0;
      
      const status = policy.policyStatus || 'Unknown';
      grouped[agentId].statusCount[status] = (grouped[agentId].statusCount[status] || 0) + 1;
    });
    
    return Object.values(grouped);
  }, [splits]);

  // Filter agents
  const filteredAgents = useMemo(() => {
    let filtered = agentsData;
    
    // Search filter
    if (agentSearch) {
      const searchLower = agentSearch.toLowerCase();
      filtered = filtered.filter(agent => 
        agent.name.toLowerCase().includes(searchLower)
      );
    }
    
    // Date filter - filter agents that have at least one split in date range
    if (agentDateRange && agentDateRange.from && agentDateRange.to) {
      filtered = filtered.filter(agent => {
        return splits.some(split => {
          const policy = split._policydata_of_split?.[0] || {};
          const policyAgentId = policy.ref_agent_owner || 'unknown';
          if (policyAgentId !== agent.id) return false;
          
          const createdAt = policy.created_at;
          if (!createdAt) return false;
          
          const dateInMs = createdAt < 10000000000 ? createdAt * 1000 : createdAt;
          const policyDate = new Date(dateInMs);
          policyDate.setHours(0, 0, 0, 0);
          
          const startDate = new Date(agentDateRange.from);
          startDate.setHours(0, 0, 0, 0);
          
          const endDate = new Date(agentDateRange.to);
          endDate.setHours(23, 59, 59, 999);
          
          return policyDate >= startDate && policyDate <= endDate;
        });
      });
    }
    
    return filtered;
  }, [agentsData, agentSearch, agentDateRange, splits]);

  // Paginate agents
  const paginatedAgents = useMemo(() => {
    const start = (agentPage - 1) * agentsPerPage;
    return filteredAgents.slice(start, start + agentsPerPage);
  }, [filteredAgents, agentPage, agentsPerPage]);

  const agentTotalPages = Math.ceil(filteredAgents.length / agentsPerPage);

  // Get policies for selected agent or all
  const agentFilteredSplits = useMemo(() => {
    if (!selectedAgent) return splits;
    
    return splits.filter(split => {
      const policy = split._policydata_of_split?.[0] || {};
      const policyAgentId = policy.ref_agent_owner || 'unknown';
      return policyAgentId === selectedAgent.id;
    });
  }, [splits, selectedAgent]);

  // Filter splits
  const filteredSplits = useMemo(() => {
    let filtered = agentFilteredSplits;
    
    // Search filter
    if (splitSearch) {
      const searchLower = splitSearch.toLowerCase();
      filtered = filtered.filter(split => {
        const policy = split._policydata_of_split?.[0] || {};
        return (
          policy.policy_number?.toLowerCase().includes(searchLower) ||
          policy.client?.toLowerCase().includes(searchLower) ||
          policy.policyAgentowner_name?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Status filter (multi-select)
    if (splitStatus && splitStatus.length > 0) {
      filtered = filtered.filter(split => {
        const policy = split._policydata_of_split?.[0] || {};
        return splitStatus.includes(policy.policyStatus);
      });
    }
    
    // Paid status filter (multi-select)
    if (splitPaidStatus && splitPaidStatus.length > 0) {
      filtered = filtered.filter(split => {
        const policy = split._policydata_of_split?.[0] || {};
        return splitPaidStatus.includes(policy.paid_status);
      });
    }
    
    // Date filter
    if (splitDateRange && splitDateRange.from && splitDateRange.to) {
      filtered = filtered.filter(split => {
        const policy = split._policydata_of_split?.[0] || {};
        const createdAt = policy.created_at;
        if (!createdAt) return false;
        
        const dateInMs = createdAt < 10000000000 ? createdAt * 1000 : createdAt;
        const policyDate = new Date(dateInMs);
        policyDate.setHours(0, 0, 0, 0);
        
        const startDate = new Date(splitDateRange.from);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(splitDateRange.to);
        endDate.setHours(23, 59, 59, 999);
        
        return policyDate >= startDate && policyDate <= endDate;
      });
    }
    
    return filtered;
  }, [agentFilteredSplits, splitSearch, splitStatus, splitPaidStatus, splitDateRange]);

  // Paginate splits
  const paginatedSplits = useMemo(() => {
    const start = (splitPage - 1) * splitsPerPage;
    return filteredSplits.slice(start, start + splitsPerPage);
  }, [filteredSplits, splitPage, splitsPerPage]);

  const splitTotalPages = Math.ceil(filteredSplits.length / splitsPerPage);

  // Get unique statuses
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    splits.forEach(split => {
      const policy = split._policydata_of_split?.[0] || {};
      if (policy.policyStatus) statuses.add(policy.policyStatus);
    });
    return Array.from(statuses);
  }, [splits]);

  const uniquePaidStatuses = useMemo(() => {
    const paidStatuses = new Set();
    splits.forEach(split => {
      const policy = split._policydata_of_split?.[0] || {};
      if (policy.paid_status) paidStatuses.add(policy.paid_status);
    });
    return Array.from(paidStatuses);
  }, [splits]);

  // Recalculate dashboard stats based on filtered splits
  const filteredDashboardStats = useMemo(() => {
    const filteredSplitsData = filteredSplits.map(split => {
      const policy = split._policydata_of_split?.[0] || {};
      return {
        ...split,
        _policydata_of_split: [policy]
      };
    });
    return calculateDashboardStats(filteredSplitsData);
  }, [filteredSplits]);

  // Handler for dashboard card clicks to filter
  const handleDashboardFilter = (filterType) => {
    switch(filterType) {
      case 'total':
        // Clear all filters to show all splits
        setSplitSearch('');
        setSplitStatus([]);
        setSplitPaidStatus([]);
        setSplitDateRange(null);
        setSplitPage(1);
        break;
      case 'approved':
        // Filter to approved/active policies
        const approvedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('approved') || s.toLowerCase().includes('active')
        );
        setSplitStatus(approvedStatuses);
        setSplitPage(1);
        break;
      case 'underwriting':
        // Filter to underwriting policies
        const underwritingStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('underwriting')
        );
        setSplitStatus(underwritingStatuses);
        setSplitPage(1);
        break;
      case 'cancelled':
        // Filter to cancelled before draft
        const cancelledStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('cancelled before draft')
        );
        setSplitStatus(cancelledStatuses);
        setSplitPage(1);
        break;
      case 'declined':
        // Filter to declined
        const declinedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('declined')
        );
        setSplitStatus(declinedStatuses);
        setSplitPage(1);
        break;
      case 'lapsedPending':
        // Filter to lapsed pending
        const lapsedPendingStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('lapsed pending')
        );
        setSplitStatus(lapsedPendingStatuses);
        setSplitPage(1);
        break;
      case 'lapsed':
        // Filter to lapsed (but not lapsed pending)
        const lapsedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase() === 'lapsed'
        );
        setSplitStatus(lapsedStatuses);
        setSplitPage(1);
        break;
      case 'notTaken':
        // Filter to not taken
        const notTakenStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('not taken')
        );
        setSplitStatus(notTakenStatuses);
        setSplitPage(1);
        break;
      default:
        break;
    }
  };

  const toggleSplit = (splitId) => {
    setExpandedSplits(prev => ({
      ...prev,
      [splitId]: !prev[splitId]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const dateInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    return new Date(dateInMs).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-800">My Splits</h1>
        <p className="text-sm text-neutral-600 mt-1">
          View and manage your policy splits
        </p>
      </div>

      {/* Dashboard Stats - Modern Design */}
      <div className="grid grid-cols-4 gap-6 mt-6">
        {/* Total Splits - Hero Card */}
        <button 
          onClick={() => handleDashboardFilter('total')}
          className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20 cursor-pointer hover:scale-105 text-left"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Splits</p>
              <TrendingUp className="w-5 h-5 text-[#b8941e]" />
            </div>
            <p className="text-4xl font-black text-[#b8941e] mb-1">{filteredDashboardStats.totalSplits}</p>
            <p className="text-xs text-neutral-600 font-medium">Click to show all</p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10">
            <div className="w-24 h-24 bg-[#d4af37] rounded-full"></div>
          </div>
        </button>

        {/* Approved */}
        <button 
          onClick={() => handleDashboardFilter('approved')}
          className="relative overflow-hidden bg-gradient-to-br from-emerald-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 text-left"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Approved</p>
              <CheckCircle2 className="w-5 h-5 text-green-700" />
            </div>
            <p className="text-3xl font-black text-green-900 mb-1">{filteredDashboardStats.approved.count}</p>
            <p className="text-sm font-bold text-green-700">${filteredDashboardStats.approved.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <CheckCircle2 className="w-32 h-32 text-green-900" />
          </div>
        </button>

        {/* Underwriting */}
        <button 
          onClick={() => handleDashboardFilter('underwriting')}
          className="relative overflow-hidden bg-gradient-to-br from-blue-400 via-blue-300 to-blue-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 text-left"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Underwriting</p>
              <Clock className="w-5 h-5 text-blue-700" />
            </div>
            <p className="text-3xl font-black text-blue-900 mb-1">{filteredDashboardStats.underwriting.count}</p>
            <p className="text-sm font-bold text-blue-700">${filteredDashboardStats.underwriting.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <Clock className="w-32 h-32 text-blue-900" />
          </div>
        </button>

        {/* Total Premium */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Total Premium</p>
              <DollarSign className="w-5 h-5 text-amber-700" />
            </div>
            <p className="text-2xl font-black text-amber-900 mb-1">${filteredDashboardStats.totalAnnualPremium.toLocaleString()}</p>
            <p className="text-xs text-amber-700 font-medium">Annual Revenue</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <DollarSign className="w-32 h-32 text-amber-900" />
          </div>
        </div>
      </div>

      {/* Dashboard Stats - Row 2 - Compact Status Cards */}
      <div className="grid grid-cols-5 gap-4 mt-4">
        <button 
          onClick={() => handleDashboardFilter('cancelled')}
          className="bg-white border-2 border-red-200 p-4 rounded-xl hover:border-red-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
        >
          <p className="text-xs font-semibold text-red-600 mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-red-700">{filteredDashboardStats.cancelledBeforeDraft.count}</p>
          <p className="text-xs text-red-500 font-medium">${filteredDashboardStats.cancelledBeforeDraft.premium.toLocaleString()}</p>
        </button>
        <button 
          onClick={() => handleDashboardFilter('declined')}
          className="bg-white border-2 border-orange-200 p-4 rounded-xl hover:border-orange-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
        >
          <p className="text-xs font-semibold text-orange-600 mb-1">Declined</p>
          <p className="text-2xl font-bold text-orange-700">{filteredDashboardStats.declined.count}</p>
          <p className="text-xs text-orange-500 font-medium">${filteredDashboardStats.declined.premium.toLocaleString()}</p>
        </button>
        <button 
          onClick={() => handleDashboardFilter('lapsedPending')}
          className="bg-white border-2 border-yellow-200 p-4 rounded-xl hover:border-yellow-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
        >
          <p className="text-xs font-semibold text-yellow-600 mb-1">Lapsed Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{filteredDashboardStats.lapsedPending.count}</p>
          <p className="text-xs text-yellow-600 font-medium">${filteredDashboardStats.lapsedPending.premium.toLocaleString()}</p>
        </button>
        <button 
          onClick={() => handleDashboardFilter('lapsed')}
          className="bg-white border-2 border-purple-200 p-4 rounded-xl hover:border-purple-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
        >
          <p className="text-xs font-semibold text-purple-600 mb-1">Lapsed</p>
          <p className="text-2xl font-bold text-purple-700">{filteredDashboardStats.lapsed.count}</p>
          <p className="text-xs text-purple-500 font-medium">${filteredDashboardStats.lapsed.premium.toLocaleString()}</p>
        </button>
        <button 
          onClick={() => handleDashboardFilter('notTaken')}
          className="bg-white border-2 border-neutral-300 p-4 rounded-xl hover:border-neutral-500 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
        >
          <p className="text-xs font-semibold text-neutral-600 mb-1">Not Taken</p>
          <p className="text-2xl font-bold text-neutral-700">{filteredDashboardStats.notTaken.count}</p>
          <p className="text-xs text-neutral-500 font-medium">${filteredDashboardStats.notTaken.premium.toLocaleString()}</p>
        </button>
      </div>

      {/* Two Container Layout */}
      <div className="flex gap-6" style={{ minHeight: '600px' }}>
        {/* Left Container - Agents Table */}
        <Card className="border-0 shadow-sm rounded-xl" style={{ width: '40%' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-neutral-800">Agents</h2>
                <p className="text-xs text-neutral-500 mt-1">{filteredAgents.length} total</p>
              </div>
            </div>

            {/* Agent Filters */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                <Input
                  placeholder="Search agents..."
                  value={agentSearch}
                  onChange={(e) => {
                    setAgentSearch(e.target.value);
                    setAgentPage(1);
                  }}
                  className="pl-10 h-9 rounded-xl"
                />
              </div>
              {agentSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAgentSearch('');
                    setAgentPage(1);
                  }}
                  className="text-xs h-7 mt-2 rounded-xl"
                >
                  Clear Search
                </Button>
              )}
            </div>

            {/* Agents Table */}
            <div className="overflow-x-auto overflow-y-auto rounded-xl border border-neutral-100" style={{ maxHeight: '500px' }}>
              <table className="w-full">
                <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                  <tr>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3 first:rounded-tl-xl">Agent</th>
                    <th className="text-center text-xs font-semibold text-neutral-700 px-4 py-3">Splits</th>
                    <th className="text-right text-xs font-semibold text-neutral-700 px-4 py-3 last:rounded-tr-xl">Premium</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-white">
                  {paginatedAgents.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-8 text-xs text-neutral-400">
                        No agents found
                      </td>
                    </tr>
                  ) : (
                    paginatedAgents.map((agent) => (
                      <tr 
                        key={agent.id}
                        onClick={() => {
                          setSelectedAgent(selectedAgent?.id === agent.id ? null : agent);
                          setSplitPage(1);
                        }}
                        className={`cursor-pointer transition-all duration-200 hover:bg-neutral-50 ${
                          selectedAgent?.id === agent.id ? 'bg-[#fffef0] border-l-4 border-l-[#d4af37]' : ''
                        }`}
                      >
                        <td className="px-4 py-4 text-sm font-semibold text-neutral-800">
                          {agent.name}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Badge className="bg-[#d4af37] hover:bg-[#d4af37] text-white text-xs">
                            {agent.totalSplits}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 text-sm text-neutral-700 text-right font-medium">
                          ${agent.totalPremium.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Agent Pagination */}
            {agentTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAgentPage(prev => Math.max(1, prev - 1))}
                  disabled={agentPage === 1}
                  className="text-xs h-8 rounded-lg"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-600 font-medium">
                    Page {agentPage} of {agentTotalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Rows:</span>
                    <Select 
                      value={agentsPerPage.toString()} 
                      onValueChange={(value) => {
                        setAgentsPerPage(Number(value));
                        setAgentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAgentPage(prev => Math.min(agentTotalPages, prev + 1))}
                  disabled={agentPage === agentTotalPages}
                  className="text-xs h-8 rounded-lg"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Container - Splits Table */}
        <Card className="border-0 shadow-sm rounded-xl" style={{ width: '60%' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-neutral-800">Splits</h2>
                <p className="text-xs text-neutral-500 mt-1">
                  {selectedAgent ? `Filtered by ${selectedAgent.name}` : `${filteredSplits.length} total`}
                </p>
              </div>
              {selectedAgent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedAgent(null)}
                  className="text-xs h-7 rounded-lg"
                >
                  Clear Filter
                </Button>
              )}
            </div>

            {/* Split Filters */}
            <div className="mb-4">
              <div className="flex gap-3">
                <div style={{ width: '40vw' }}>
                  <div className="space-y-1">
                    <label className="text-xs text-neutral-500 font-medium">Quick Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                      <Input
                        placeholder="Search policies..."
                        value={splitSearch}
                        onChange={(e) => {
                          setSplitSearch(e.target.value);
                          setSplitPage(1);
                        }}
                        className="pl-10 h-9 rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                {/* Policy Status - Checkbox Multi-Select */}
                <div style={{ width: '15vw' }}>
                  <div className="space-y-1">
                    <label className="text-xs text-neutral-500 font-medium">by Policy Status</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="rounded-xl w-full h-9">
                          <Filter className="w-4 h-4 mr-2" />
                          Status {splitStatus.length > 0 && `(${splitStatus.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" align="start">
                        <div className="space-y-2">
                          <div className="font-semibold text-sm mb-2">Select Policy Status</div>
                          {uniqueStatuses.map(status => (
                            <div key={status} className="flex items-center space-x-2">
                              <Checkbox
                                id={`split-status-${status}`}
                                checked={splitStatus.includes(status)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSplitStatus([...splitStatus, status]);
                                  } else {
                                    setSplitStatus(splitStatus.filter(s => s !== status));
                                  }
                                  setSplitPage(1);
                                }}
                              />
                              <label
                                htmlFor={`split-status-${status}`}
                                className="text-sm cursor-pointer flex-1"
                              >
                                {status}
                              </label>
                            </div>
                          ))}
                          {splitStatus.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSplitStatus([]);
                                setSplitPage(1);
                              }}
                              className="w-full mt-2 text-xs"
                            >
                              Clear All
                            </Button>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Paid Status - Checkbox Multi-Select */}
                <div style={{ width: '15vw' }}>
                  <div className="space-y-1">
                    <label className="text-xs text-neutral-500 font-medium">by Paid Status</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="rounded-xl w-full h-9">
                          <Filter className="w-4 h-4 mr-2" />
                          Paid {splitPaidStatus.length > 0 && `(${splitPaidStatus.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" align="start">
                        <div className="space-y-2">
                          <div className="font-semibold text-sm mb-2">Select Paid Status</div>
                          {uniquePaidStatuses.map(status => (
                            <div key={status} className="flex items-center space-x-2">
                              <Checkbox
                                id={`split-paid-${status}`}
                                checked={splitPaidStatus.includes(status)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSplitPaidStatus([...splitPaidStatus, status]);
                                  } else {
                                    setSplitPaidStatus(splitPaidStatus.filter(s => s !== status));
                                  }
                                  setSplitPage(1);
                                }}
                              />
                              <label
                                htmlFor={`split-paid-${status}`}
                                className="text-sm cursor-pointer flex-1"
                              >
                                {status}
                              </label>
                            </div>
                          ))}
                          {splitPaidStatus.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSplitPaidStatus([]);
                                setSplitPage(1);
                              }}
                              className="w-full mt-2 text-xs"
                            >
                              Clear All
                            </Button>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div style={{ width: '30vw' }}>
                  <div className="space-y-1">
                    <label className="text-xs text-neutral-500 font-medium">by Date Created</label>
                    <DateRangePicker
                      value={splitDateRange}
                      onChange={(range) => {
                        setSplitDateRange(range);
                        setSplitPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Clear Filters Button - Highlighted */}
              {(splitSearch || splitStatus.length > 0 || splitPaidStatus.length > 0 || splitDateRange) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setSplitSearch('');
                    setSplitStatus([]);
                    setSplitPaidStatus([]);
                    setSplitDateRange(null);
                    setSplitPage(1);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 w-auto px-4 mt-2"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Splits Table */}
            <div className="overflow-x-auto overflow-y-auto rounded-xl border border-neutral-100" style={{ maxHeight: '500px' }}>
              <table className="w-full">
                <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                  <tr>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3 first:rounded-tl-xl">Client</th>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Policy #</th>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Policy Owner</th>
                    <th className="text-center text-xs font-semibold text-neutral-700 px-4 py-3">Split %</th>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3">Source</th>
                    <th className="text-right text-xs font-semibold text-neutral-700 px-4 py-3 last:rounded-tr-xl">Annual Premium</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-white">
                  {paginatedSplits.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-xs text-neutral-400">
                        {selectedAgent ? 'No splits found for this agent' : 'No splits found'}
                      </td>
                    </tr>
                  ) : (
                    paginatedSplits.map((split) => {
                      const policy = split._policydata_of_split?.[0] || {};
                      return (
                        <React.Fragment key={split.id}>
                          <tr 
                            onClick={() => toggleSplit(split.id)}
                            className="cursor-pointer hover:bg-neutral-50 transition-all duration-200"
                          >
                            <td className="px-4 py-4 text-sm text-neutral-700">
                              {policy.client || 'N/A'}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                {expandedSplits[split.id] ? (
                                  <ChevronDown className="w-4 h-4 text-[#d4af37]" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                                )}
                                <span className="text-sm font-medium text-neutral-800">
                                  #{policy.policy_number || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-neutral-700">
                              {policy.policyAgentowner_name || 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-center">
                              <Badge variant="outline" className="text-xs font-medium">
                                {split.agent_on_split_percent || 0}%
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-col gap-1">
                                <Badge className="text-xs rounded-full w-fit">
                                  {policy.policyStatus || 'Unknown'}
                                </Badge>
                                {policy.paid_status && (
                                  <Badge variant="outline" className="text-xs rounded-full w-fit border-blue-300 text-blue-600">
                                    {policy.paid_status}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-neutral-700">
                              {policy.sourceName || 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-sm text-neutral-700 text-right font-medium">
                              ${parseFloat(policy.annual_premium || 0).toLocaleString()}
                            </td>
                          </tr>
                          {expandedSplits[split.id] && (
                            <tr key={`${split.id}-details`}>
                              <td colSpan="7" className="px-4 py-4 bg-neutral-50">
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <p className="text-xs text-neutral-500">Carrier</p>
                                    <p className="font-medium text-neutral-800">{policy.carrierName || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Carrier Product</p>
                                    <p className="font-medium text-neutral-800">{policy.carrier_product || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Phone</p>
                                    <p className="font-medium text-neutral-800">{policy.phone || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">State</p>
                                    <p className="font-medium text-neutral-800">{policy.state || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Initial Draft Date</p>
                                    <p className="font-medium text-neutral-800">{policy.initial_draft_date || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Face Amount</p>
                                    <p className="font-medium text-neutral-800">${parseFloat(policy.face_amount || 0).toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Monthly Premium</p>
                                    <p className="font-medium text-neutral-800">${parseFloat(policy.monthly_premium || 0).toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Created Date</p>
                                    <p className="font-medium text-neutral-800">{formatDate(policy.created_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500">Policy Type</p>
                                    <p className="font-medium text-neutral-800">{policy.typeName || 'N/A'}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Split Pagination */}
            {splitTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSplitPage(prev => Math.max(1, prev - 1))}
                  disabled={splitPage === 1}
                  className="text-xs h-8 rounded-lg"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-600 font-medium">
                    Page {splitPage} of {splitTotalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Rows:</span>
                    <Select 
                      value={splitsPerPage.toString()} 
                      onValueChange={(value) => {
                        setSplitsPerPage(Number(value));
                        setSplitPage(1);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSplitPage(prev => Math.min(splitTotalPages, prev + 1))}
                  disabled={splitPage === splitTotalPages}
                  className="text-xs h-8 rounded-lg"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
